import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, multiFactor, sendEmailVerification, EmailAuthProvider, linkWithCredential, onAuthStateChanged } from 'firebase/auth';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import MFAEnrollment from './MFAEnrollment';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';
import API, {
    GetUserMFATOTPSecretPathForApi,
    GetUserMFARecoveryCodesRegeneratePathForApi,
    GetUserSyncEmailVerificationPathForApi,
    GetUserForceVerifyEmailPathForApi,
} from '../../Util/api';

const isEmailVerified = (user) => {
    if (!user) return false;
    const hasPasswordProvider = user.providerData.some(p => p.providerId === 'password');
    if (!hasPasswordProvider) return true;
    return user.emailVerified;
};

// Firebase TOTP MFA requires a supported first factor (email/password or native OAuth).
// Custom token auth (e.g. Intuit) is not supported.
const supportedMFAProviders = ['password', 'google.com', 'apple.com'];
const isMFASupported = (user) => {
    if (!user) return false;
    return user.providerData.some(p => supportedMFAProviders.includes(p.providerId));
};

const MFAUserSettings = () => {
    const [enrolledFactors, setEnrolledFactors] = useState([]);
    const [showEnrollment, setShowEnrollment] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [unenrollLoading, setUnenrollLoading] = useState(false);
    const [mfaSupported, setMfaSupported] = useState(true);
    const [emailVerified, setEmailVerified] = useState(null);
    const [sendingVerification, setSendingVerification] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [regeneratedCodes, setRegeneratedCodes] = useState([]);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [settingPassword, setSettingPassword] = useState(false);

    const refreshMFAStatus = useCallback(async () => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (user) {
                setMfaSupported(isMFASupported(user));
                setEmailVerified(isEmailVerified(user));
                const initialFactors = multiFactor(user).enrolledFactors;
                setEnrolledFactors(initialFactors);

                // Sync email verification from OAuth providers
                try {
                    await API.post(GetUserSyncEmailVerificationPathForApi());
                    await user.reload();
                    const freshUser = auth.currentUser;
                    if (freshUser) {
                        const factors = multiFactor(freshUser).enrolledFactors;
                        setEnrolledFactors(factors);
                        setEmailVerified(isEmailVerified(freshUser));
                    }
                } catch (err) {
                    console.debug('Email verification sync failed (non-critical):', err);
                }
            } else {
                setEmailVerified(false);
            }
        } catch (err) {
            console.error('Error refreshing MFA status:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshMFAStatus();
    }, [refreshMFAStatus]);

    useEffect(() => {
        try {
            const auth = getAuth();
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (user) {
                    refreshMFAStatus();
                } else {
                    setEmailVerified(null);
                    setEnrolledFactors([]);
                }
            });
            return () => unsubscribe();
        } catch (err) {
            return () => {};
        }
    }, [refreshMFAStatus]);

    const handleUnenroll = async (factor) => {
        setUnenrollLoading(true);
        setError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            await multiFactor(user).unenroll(factor);
            // Clean up server-side TOTP secret
            try { await API.delete(GetUserMFATOTPSecretPathForApi()); } catch (e) { /* non-critical */ }
            setSuccess('MFA has been disabled.');
            refreshMFAStatus();
        } catch (err) {
            console.error('Error unenrolling MFA:', err);
            if (err.code === 'auth/requires-recent-login') {
                setError('Please sign out and sign back in before disabling MFA.');
            } else {
                setError(err.message || 'Failed to disable MFA.');
            }
        } finally {
            setUnenrollLoading(false);
        }
    };

    const handleEnrollmentComplete = () => {
        setShowEnrollment(false);
        setSuccess('MFA has been enabled successfully!');
        refreshMFAStatus();
    };

    const handleRegenerateCodes = async () => {
        setRegenerateLoading(true);
        setError('');
        try {
            const response = await API.post(GetUserMFARecoveryCodesRegeneratePathForApi());
            setRegeneratedCodes(response.data.recoveryCodes);
        } catch (err) {
            console.error('Error regenerating recovery codes:', err);
            setError('Failed to regenerate recovery codes.');
        } finally {
            setRegenerateLoading(false);
        }
    };

    const handleSendVerificationEmail = async () => {
        setSendingVerification(true);
        setError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            await sendEmailVerification(user);
            setSuccess('Verification email sent! Please check your inbox and verify your email address.');
        } catch (err) {
            console.error('Error sending verification email:', err);
            setError(err.message || 'Failed to send verification email.');
        } finally {
            setSendingVerification(false);
        }
    };

    const handleCheckVerification = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const initialVerified = isEmailVerified(user);
                setEmailVerified(initialVerified);

                if (initialVerified) {
                    setSuccess('Email verified successfully! You can now enable MFA.');
                    setIsLoading(false);
                    return;
                }

                try {
                    await API.post(GetUserSyncEmailVerificationPathForApi());
                    await user.reload();
                    const freshUser = auth.currentUser;
                    if (freshUser) {
                        const verified = isEmailVerified(freshUser);
                        setEmailVerified(verified);
                        if (!verified) {
                            setError('Email not verified yet. Please check your inbox and click the verification link.');
                        } else {
                            setSuccess('Email verified successfully! You can now enable MFA.');
                        }
                    }
                } catch (err) {
                    console.error('Error syncing email verification:', err);
                    setError('Failed to sync verification status. If you signed in with Google, you should already be verified.');
                }
            }
        } catch (err) {
            console.error('Error checking verification:', err);
            setError('Failed to check verification status. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetPassword = async () => {
        setError('');
        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setSettingPassword(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            let email = user.email;
            if (!email) {
                const tokenResult = await user.getIdTokenResult();
                email = tokenResult.claims.email;
            }
            if (!email) {
                setError('Unable to determine your email address. Please sign out and sign back in.');
                setSettingPassword(false);
                return;
            }
            const credential = EmailAuthProvider.credential(email, newPassword);
            await linkWithCredential(user, credential);
            await API.post(GetUserForceVerifyEmailPathForApi());
            await user.reload();
            setMfaSupported(true);
            setEmailVerified(true);
            setNewPassword('');
            setConfirmPassword('');
            setSuccess('Password set successfully! You can now enable MFA.');
        } catch (err) {
            console.error('Error setting password:', err);
            if (err.code === 'auth/requires-recent-login') {
                setError('Your session has expired. Please sign out and sign back in, then try again.');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak. Please choose a stronger password.');
            } else if (err.code === 'auth/email-already-in-use') {
                setError('A password is already set for this account. Please sign out and sign in with your password.');
            } else {
                setError(err.message || 'Failed to set password.');
            }
        } finally {
            setSettingPassword(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                <CircularProgress />
            </div>
        );
    }

    if (showEnrollment) {
        return (
            <div style={{ padding: 16 }}>
                <MFAEnrollment
                    onEnrollmentComplete={handleEnrollmentComplete}
                    onCancel={() => setShowEnrollment(false)}
                />
            </div>
        );
    }

    const hasMFA = enrolledFactors.length > 0;

    return (
        <Paper style={{ padding: 24, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
                Account Security
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" style={{ marginBottom: 16 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Typography variant="subtitle1" gutterBottom>
                Multi-Factor Authentication
            </Typography>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Typography variant="body2">
                    Status:
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: hasMFA ? '#4caf50' : undefined }}>
                    {hasMFA ? 'Enabled' : 'Not enabled'}
                </Typography>
            </div>

            {hasMFA ? (
                <div>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Your account is protected with an authenticator app.
                    </Typography>
                    {enrolledFactors.map((factor, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Typography variant="body2">
                                {factor.displayName || 'Authenticator App'}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleUnenroll(factor)}
                                disabled={unenrollLoading}
                                style={{ color: '#f44336', borderColor: '#f44336' }}
                            >
                                {unenrollLoading ? <CircularProgress size={16} /> : 'Remove'}
                            </Button>
                        </div>
                    ))}

                    <Divider style={{ margin: '16px 0' }} />

                    {regeneratedCodes.length > 0 ? (
                        <RecoveryCodesDisplay
                            codes={regeneratedCodes}
                            onAcknowledge={() => setRegeneratedCodes([])}
                            title="New Recovery Codes"
                        />
                    ) : (
                        <div>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                                Recovery codes let you sign in if you lose access to your authenticator app.
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleRegenerateCodes}
                                disabled={regenerateLoading}
                            >
                                {regenerateLoading ? <CircularProgress size={16} /> : 'Regenerate Recovery Codes'}
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    {!mfaSupported ? (
                        <div>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                To enable MFA, you need to set a password for your account.
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="password"
                                label="New password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                style={{ marginBottom: 12 }}
                            />
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="password"
                                label="Confirm password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') handleSetPassword();
                                }}
                                style={{ marginBottom: 16 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSetPassword}
                                disabled={settingPassword}
                            >
                                {settingPassword ? <CircularProgress size={20} /> : 'Set Password & Continue'}
                            </Button>
                        </div>
                    ) : emailVerified === null ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                            <CircularProgress size={24} />
                        </div>
                    ) : emailVerified === false ? (
                        <div>
                            <Alert severity="warning" style={{ marginBottom: 16 }}>
                                You must verify your email address before enabling MFA.
                            </Alert>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                Please check your inbox for a verification email, or click below to send a new one.
                            </Typography>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleSendVerificationEmail}
                                    disabled={sendingVerification}
                                >
                                    {sendingVerification ? <CircularProgress size={16} /> : 'Send Verification Email'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCheckVerification}
                                    disabled={isLoading}
                                >
                                    I've Verified My Email
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                Add an extra layer of security by requiring a code from an authenticator app when signing in.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowEnrollment(true)}
                            >
                                Enable MFA
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </Paper>
    );
};

export default MFAUserSettings;
